import React, { PropsWithChildren } from 'react';
import { useTheme } from 'styled-components';
import * as S from './BottomSheet.styles';
import { CloseIcon } from '@/components/UI/Icons/Icons';
import { Portal } from '@/components/UI/Portal/Portal';
import { Overlay } from '@/components/UI/Overlay';
import { useBodyOverflow } from '@/hooks/useBodyOverflow';
import { useAnimatedVisibility } from '@/hooks/useAnimatedVisibility';

interface BottomSheetProps {
  isOpen: boolean;
  close: () => void;
  title?: string;
  headPaddingTop?: number;
  hasHeadBorder?: boolean;
  isTitleCentered?: boolean;
  isInPortal?: boolean;
  dataTestId?: string;
  hasOverlay?: boolean;
  isPageLocked?: boolean;
  onCloseClick?: () => void;
  background?: string;
}

export const BottomSheet = ({
  children,
  isOpen,
  close,
  title,
  headPaddingTop = 0,
  isPageLocked = false,
  isTitleCentered = true,
  hasHeadBorder = true,
  hasOverlay = true,
  isInPortal,
  dataTestId,
  onCloseClick,
  background,
}: PropsWithChildren<BottomSheetProps>) => {
  const { isVisible, onAnimationEnd } = useAnimatedVisibility(isOpen);
  const theme = useTheme();
  useBodyOverflow(isVisible && isPageLocked);

  function renderBottomSheet() {
    return (
      <>
        {hasOverlay && <Overlay isOpen={isOpen} onClick={close} />}
        <S.Box
          $isOpen={isOpen}
          $background={background}
          data-testid={dataTestId ?? 'bottom-sheet'}
          onAnimationEnd={onAnimationEnd}
        >
          {title && (
            <S.Head
              $headPaddingTop={headPaddingTop}
              $isTitleCentered={isTitleCentered}
              $hasBorder={hasHeadBorder}
            >
              <S.Title $variant="3">{title}</S.Title>
              <S.Close appearance="transparent" onClick={onCloseClick ?? close}>
                <CloseIcon fill={theme.palette.secondary.labels} />
              </S.Close>
            </S.Head>
          )}
          <S.Body>{children}</S.Body>
        </S.Box>
      </>
    );
  }

  if (!isVisible) return null;

  if (isInPortal) {
    return <Portal>{renderBottomSheet()}</Portal>;
  }

  return renderBottomSheet();
};
